import { useAppStore } from '@/store/app-store';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import TrackingContext from './tracking-context';

interface TrackingData {
  key: string;
  payload: any;
  action?: string;
}

export interface TrackingDataItem {
  key: string;
  payload: any;
}

export interface TrackingPage {
  items?: TrackingDataItem[];
  key?: string;
  payload?: any;
  breadcrumb?: any;
}

const initialDigitalData = () => {
  // @ts-ignore
  window.digitalData = {};
  // @ts-ignore
  return window.digitalData;
};

export function useEventTracking() {
  const dispatch = useCallback((data: TrackingData) => {
    // @ts-ignore
    const digitalData = window.digitalData || initialDigitalData();
    data.key && (digitalData[data.key] = data.payload);
    if (sessionStorage['track']) console.log('useEventTracking', digitalData);
    document.dispatchEvent(new CustomEvent(data.action));
  }, []);

  const trackEvent = useCallback(
    (data: any) => {
      dispatch(data);
    },
    [dispatch]
  );

  const trackEventEffect = (data, shouldTrack = false) => {
    useEffect(() => {
      if (shouldTrack) {
        trackEvent(data);
      }
    }, [shouldTrack]);
  };

  return useMemo(
    () => ({
      trackEvent,
      trackEventEffect,
    }),
    [trackEvent, trackEventEffect]
  );
}

export function useShouldTrackPage(initialValue = false) {
  const [shouldTrackPage, setShouldTrackPage] = useState(initialValue);
  return { shouldTrackPage, setShouldTrackPage };
}

export function usePageTracking(trackingPage?: TrackingPage, shouldTrackPage = true) {
  const contextValue = useContext(TrackingContext);
  const { userInfo } = useAppStore();

  useEffect(() => {
    if (shouldTrackPage && userInfo && Object.keys(userInfo).length > 0) {
      const digitalData = initialDigitalData();

      if (userInfo.cdcUid) {
        digitalData.user = {
          gigyaId: userInfo.cdcUid,
          loginStatus: 'logged in',
        };
      } else {
        digitalData.user = {
          loginStatus: 'anonymous',
        };
      }
      contextValue &&
        Object.keys(contextValue).forEach((key) => {
          const value = contextValue[key];
          digitalData[key] = value;
        });

      if (trackingPage) {
        if (Array.isArray(trackingPage.items)) {
          trackingPage.items.forEach((data) => {
            digitalData[data.key] = data.payload;
          });
        } else if (trackingPage.key && trackingPage.payload) {
          digitalData[trackingPage.key] = trackingPage.payload;
        }
      }
      if (digitalData.page) {
        const url = typeof window !== 'undefined' && window.location.href ? window.location.href : '';
        digitalData.page.url = url;

        if (trackingPage?.breadcrumb && Array.isArray(trackingPage.breadcrumb) && trackingPage.breadcrumb.length > 0) {
          digitalData.page.breadcrumbs = trackingPage.breadcrumb;
        }
      }
      setTimeout(() => {
        if (sessionStorage['track']) console.log('usePageTracking', digitalData);
        document.dispatchEvent(new CustomEvent('pageload'));
      }, 1000);
    }
  }, [userInfo, shouldTrackPage]);
}
